import diagnostics from "@/services/diagnostics";
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, watch } from "vue";
import { useRouter } from "vue-router";
import realtime from "@/services/realtime";
import telegram from "@/services/telegram";
import { getOpponentInfo } from "@/services/api";
import { acquireCamera, cameraErrorMessage, releaseCamera } from "@/services/media";
import { createPeerSession, REMOTE_VOLUME } from "@/services/webrtc";
import {
  captureFrameJpeg,
  createViolationMonitor,
  isChunkLoadError,
  loadFaceLandmarker,
} from "@/services/faceDetection";
import { useGameStore } from "@/stores/game";
import { useSessionStore } from "@/stores/session";
import { useToast } from "@/composables/useToast";
import { createRoundRecording, buildRecordedViolation } from "@/services/roundRecording";
import { usePostRound } from "./usePostRound";
import { createRoundSounds, stopInterfaceSound } from "@/services/roundSounds";

/**
 * Логика экрана игры (/game): фазы intro → calibration → playing (+ проверка) → результат,
 * WebRTC, детекция нарушений, готовность и сторожа (CONTRACT §3, §5, §6).
 *
 *   const s = useGameSession({ localVideo, remoteVideo })   // ref'ы на <video>
 *
 * Фазы (phase): 'intro' | 'calibration' | 'playing' | 'ended'. Проверка нарушения — флаг checking
 * поверх calibration/playing (в game-сторе отражается как phase 'processing').
 * Все подписки, таймеры, RTCPeerConnection, цикл детекции и камера освобождаются при размонтировании
 * (модель FaceLandmarker остаётся загруженной для следующей игры).
 */

const INTRO_SLOW_MS = 8000;
const INTRO_TIMEOUT_MS = 60000;
const COUNTDOWN_SECONDS = 5;
const CALIBRATION_MS = 4600;
const INSTRUCTION_MS = 3000;
const ALERT_MS = 3500;
const PROCESSING_SYNC_MS = 20000;
const PROCESSING_GIVE_UP_MS = 10000;
/** Ответ PROCESSING на sync_game: сервер ещё проверяет — один раз ждём дольше, но не больше общего предела. */
const PROCESSING_EXTEND_MS = 20000;
const PROCESSING_MAX_MS = 50000;
const OPPONENT_LEFT_HOME_MS = 4000;
const READY_RESYNC_MS = 20000;

const PREVIEW_ELAPSED = 37;

export function useGameSession({ localVideo, remoteVideo }) {
  const router = useRouter();
  const game = useGameStore();
  const session = useSessionStore();
  const toast = useToast();

  const uuid = game.uuid;
  const mode = game.mode === "MOUTH" ? "MOUTH" : "BLINK";
  const initiator = game.isInitiator;
  const preview = game.preview;

  /* ------------------------------------------------------------ состояние */

  const phase = ref("intro");
  const checking = ref(false);
  const alertVisible = ref(false);
  const startBanner = ref(false);
  const startBannerFading = ref(false);
  const checkingSource = ref(null); // 'me' | 'opponent'
  const countdown = ref(COUNTDOWN_SECONDS);
  const countdownProgress = ref(0);
  const elapsed = ref(0);
  const faceDetected = ref(false);
  const cameraReady = ref(false);
  const modelStatus = ref("loading"); // 'loading' | 'ready' | 'error'
  const rtcStage = ref("none"); // 'none' | 'peer' | 'offer' | 'remote' | 'connected'
  const rtcState = ref("new");
  const remoteVisible = ref(false);
  const readyReceived = ref(false);
  const waitingLong = ref(false);
  const overlay = ref(null);
  /** Dev tools: последние метрики детектора (обновляются ~4 раза в секунду). */
  const devMetrics = shallowRef(null);

  let roundFinal = false;
  let pendingPostRound = null;
  let recording = null;
  const postRound = usePostRound({
    uuid, game, session,
    audioConnected: () => !["disconnected", "failed", "closed"].includes(rtcState.value),
    onResult: () => { finish(); router.replace("/result"); },
    onRematch: (created) => {
      const previousOpponent = game.opponent;
      finish();
      game.startGame(created);
      game.preview = null;
      if (previousOpponent) game.setOpponent(previousOpponent);
      router.replace({ path: "/game", query: { round: created.uuid } });
    },
  });

  let peer = null;
  let monitor = null;
  let localStream = null;
  let offs = [];
  const timers = new Set();
  const intervals = new Set();
  let countdownInterval = null;
  let preparationAt = null;
  let preparationOffset = 0;
  let preparationStarted = false;
  let calibrationReady = false;
  let startTimer = null;
  let alertTimer = null;
  let alertEndsAt = 0;
  let alertServerEnd = null;
  let processingIdentity = null;
  let lastCompletedProcessing = -Infinity;
  let pendingFinal = null;
  let pendingFinalReceivedAt = 0;
  let pendingResume = false;
  let playStartedAt = 0;
  let watchdogTimer = null;
  let checkingSince = 0;
  let checkingSyncSent = false;
  let checkingExtended = false;
  let readyResyncTimer = null;
  let homeTimer = null;
  let disposed = false;
  let finished = false;
  let tornDown = false;
  let leaving = false;
  let unmuteHandler = null;
  let trackEndedHandler = null;
  let detectorWarned = false;
  let devPeak = null;
  let devMetricsAt = 0;
  const diagnosticRound = diagnostics.nextRound(mode, preview);
  const releaseDiagnostics = diagnostics.registerSource("game", () => ({
    round: diagnosticRound, mode, preview: !!preview, phase: phase.value, checking: checking.value,
    timers: timers.size + Number(homeTimer !== null), intervals: intervals.size, subscriptions: offs.length,
    callbacks: Number(!!unmuteHandler) + Number(!!trackEndedHandler),
    localWidth: localVideo.value?.videoWidth, localHeight: localVideo.value?.videoHeight,
    remoteWidth: remoteVideo.value?.videoWidth, remoteHeight: remoteVideo.value?.videoHeight,
    localFrames: localVideo.value?.getVideoPlaybackQuality?.().totalVideoFrames,
    remoteFrames: remoteVideo.value?.getVideoPlaybackQuality?.().totalVideoFrames,
    localDropped: localVideo.value?.getVideoPlaybackQuality?.().droppedVideoFrames,
    remoteDropped: remoteVideo.value?.getVideoPlaybackQuality?.().droppedVideoFrames,
  }));

  const roundSounds = createRoundSounds(() => session.notificationsEnabled && !finished && !roundFinal &&
    phase.value === "playing" && !checking.value && realtime.status.value === "open" &&
    !["disconnected", "failed", "closed"].includes(rtcState.value));
  const alertSounds = createRoundSounds(() => session.notificationsEnabled && !finished &&
    realtime.status.value === "open" && !["disconnected", "failed", "closed"].includes(rtcState.value));
  const stopAudioWatch = watch(() => [phase.value, checking.value, realtime.status.value, rtcState.value],
    () => { roundSounds.refresh(); alertSounds.refresh(); }, { flush: "sync" });

  /* ------------------------------------------------------------- таймеры */

  function later(fn, ms) {
    const id = setTimeout(() => {
      timers.delete(id);
      fn();
    }, ms);
    timers.add(id);
    return id;
  }

  function cancel(id) {
    if (id === null || id === undefined) return;
    clearTimeout(id);
    timers.delete(id);
  }

  function every(fn, ms) {
    const id = setInterval(fn, ms);
    intervals.add(id);
    return id;
  }

  function stopEvery(id) {
    if (id === null || id === undefined) return;
    clearInterval(id);
    intervals.delete(id);
  }

  function clearAllTimers() {
    timers.forEach((id) => clearTimeout(id));
    timers.clear();
    intervals.forEach((id) => clearInterval(id));
    intervals.clear();
    countdownInterval = null;
    watchdogTimer = null;
    readyResyncTimer = null;
  }

  /* ---------------------------------------------------------- вычисления */

  const me = computed(() => session.me);
  const opponent = computed(() => game.opponent);

  /** Прогресс подключения для линии под заголовком intro. */
  const connectionProgress = computed(() => {
    let p = 0.08;
    if (cameraReady.value) p += 0.16;
    if (modelStatus.value === "ready") p += 0.18;
    if (["offer", "remote", "connected"].includes(rtcStage.value)) p += 0.1;
    if (["remote", "connected"].includes(rtcStage.value)) p += 0.1;
    if (rtcStage.value === "connected") p += 0.18;
    if (readyReceived.value) p += 0.2;
    return Math.min(1, p);
  });

  const introStatus = computed(() => {
    if (!cameraReady.value) return "Включаем камеру…";
    if (modelStatus.value === "loading") return "Загружаем распознавание лица…";
    if (modelStatus.value === "error") return "Распознавание лица не загрузилось";
    if (rtcStage.value !== "connected") return "Соединяемся с соперником…";
    if (!readyReceived.value) return "Ждём готовности соперника…";
    return "Начинаем!";
  });

  /** Предупреждение «Лицо не в кадре» (лица нет > 1.5 c во время игры). */
  const faceWarning = computed(
    () => phase.value === "playing" && !checking.value && modelStatus.value === "ready" && !faceDetected.value,
  );

  /* -------------------------------------------------------------- сервер */

  function sendDeviceStatus(status) {
    realtime.send({ type: "device_status_update", status, gameUUID: uuid }, { ttl: 30000 });
  }

  /**
   * Повторить флаги готовности (CONTRACT §3.4) — только пока игра не стартовала: сообщения могли
   * потеряться при обрыве. Сервер принимает их идемпотентно; QUALITY_OK — если видеосвязь уже есть.
   */
  function sendReadiness() {
    sendDeviceStatus("WS_READY");
    if (modelStatus.value === "ready") sendDeviceStatus("ML_READY");
    if (peer && peer.connected) {
      realtime.send({ type: "quality_report", status: "QUALITY_OK", gameUUID: uuid }, { ttl: 30000 });
    }
  }

  function syncGame() {
    realtime.send({ type: "sync_game", gameUUID: uuid });
  }

  /** WebSocket переподключился (в пределах grace-периода сервера): узнаём текущее состояние игры. */
  function onReconnected() {
    if (finished || disposed) return;
    if (phase.value === "intro" && !readyReceived.value) sendReadiness();
    else if (calibrationReady && !playStartedAt) sendDeviceStatus("CALIBRATION_READY");
    syncGame();
  }

  function isMine(userId) {
    return userId !== null && userId !== undefined && session.userId !== null && Number(userId) === Number(session.userId);
  }

  function onGameStatus(message) {
    if (!message || finished) return;
    if (message.gameUUID && message.gameUUID !== uuid) return;
    if ((roundFinal || pendingFinal) && !["FINAL", "OPPONENT_LEFT"].includes(message.status)) return;
    switch (message.status) {
      case "READY":
        if (message.phase === "PREPARE") {
          if (preparationStarted || phase.value !== "intro") break;
          readyReceived.value = true;
          preparationOffset = Number.isFinite(message.serverNow) ? message.serverNow - Date.now() : 0;
          preparationAt = Number.isFinite(message.preparationAt) ? message.preparationAt : Date.now() + preparationOffset;
          cancel(readyResyncTimer);
          readyResyncTimer = null;
          maybeStartCalibration();
        } else if (message.phase === "START" || (message.phase === "RESUME" && !playStartedAt && Number.isFinite(message.startAt))) {
          if (playStartedAt || startTimer || pendingFinal) break;
          const offset = Number.isFinite(message.serverNow) ? message.serverNow - Date.now() : preparationOffset;
          const at = Number.isFinite(message.startAt) ? message.startAt - offset : Date.now();
          startTimer = later(() => { startTimer = null; startPlaying(at); }, Math.max(0, at - Date.now()));
        } else if (checking.value && !pendingFinal) {
          deferResume();
        }
        break;
      case "WAITING":
        // sync: соперник найден, но игра ещё не READY — продолжаем ждать.
        break;
      case "PROCESSING":
        if (phase.value !== "playing") break;
        if (Number.isFinite(message.processingStartedAt) && message.processingStartedAt <= lastCompletedProcessing) break;
        if (Number.isFinite(message.processingStartedAt)) processingIdentity = message.processingStartedAt;
        if (!checking.value) {
          beginChecking(isMine(message.suspectUserId) ? "me" : "opponent", message);
          break;
        }
        if (isMine(message.suspectUserId)) checkingSource.value = "me";
        updateAlertDeadline(message);
        extendWatchdog();
        break;
      case "ERROR":
        if (checking.value) {
          toast.info("Не удалось проверить, продолжаем");
          deferResume();
        }
        break;
      case "FINAL":
        onFinal(message);
        break;
      case "OPPONENT_LEFT":
        endGame({ icon: "user", ...opponentLeftText(message.reason), autoHomeMs: OPPONENT_LEFT_HOME_MS });
        break;
      case "QUALITY_FAILED":
        endGame({
          icon: "warning",
          tone: "danger",
          title: "Проблемы со связью",
          text: message.reason || "Не удалось установить стабильное соединение. Попробуйте сыграть ещё раз.",
        });
        break;
      case "OPPONENT_QUALITY_FAILED":
        endGame({
          icon: "warning",
          tone: "danger",
          title: "У соперника проблемы со связью",
          text: message.reason || "Соединение с соперником нестабильно. Игра отменена.",
        });
        break;
      case "CANCELLED":
        finish();
        router.replace("/");
        break;
      default:
        break;
    }
  }

  /**
   * Тексты для OPPONENT_LEFT. Ответ на sync_game для отменённой/неизвестной игры приходит с причиной
   * «Игра прервана» / «Игра не найдена» — это не обязательно уход соперника (например, мы сами были
   * без связи дольше grace-периода сервера).
   */
  function opponentLeftText(reason) {
    const text = "Игра завершена без результата.";
    if (reason === "Игра прервана" || reason === "Игра не найдена") return { title: "Игра прервана", text };
    return {
      title: "Соперник покинул игру",
      text: reason && reason !== "Соперник покинул игру" ? reason : text,
    };
  }

  function onPostRound(message) {
    if (finished || message.gameUUID !== uuid) return;
    if (!roundFinal) {
      if (!pendingPostRound || message.revision >= pendingPostRound.revision) pendingPostRound = message;
      syncGame();
      return;
    }
    postRound.applySnapshot(message);
  }

  function onFinal(message) {
    if (finished) return;
    if (alertVisible.value) {
      const revision = message.postRound?.revision ?? 0;
      const previousRevision = pendingFinal?.postRound?.revision ?? 0;
      if (!pendingFinal || revision > previousRevision || (revision === previousRevision &&
        (message.postRound?.serverNow ?? 0) > (pendingFinal.postRound?.serverNow ?? 0))) {
        pendingFinal = message;
        pendingFinalReceivedAt = Date.now();
      }
      cancel(watchdogTimer);
      watchdogTimer = null;
      return;
    }
    if (roundFinal) {
      if (message.postRound) postRound.applySnapshot(message.postRound);
      return;
    }
    roundFinal = true;
    roundSounds.dispose();
    alertSounds.dispose();
    const myId = session.userId;
    game.setResult(message, myId);
    const outcome = game.result ? game.result.outcome : null;
    const applyResult = game.appliedResultGameUUID !== uuid;
    game.appliedResultGameUUID = uuid;
    if (applyResult && !game.privateRoom && outcome && session.me) {
      const m = session.me;
      session.patchMe(
        outcome === "WIN"
          ? { countWins: m.countWins + 1, gamesPlayed: m.gamesPlayed + 1 }
          : { countLosses: m.countLosses + 1, gamesPlayed: m.gamesPlayed + 1 },
      );
    }
    if (!game.privateRoom && message.stars !== undefined && message.stars !== null) {
      session.setStars(message.stars);
    } else if (applyResult && !game.privateRoom && outcome === "WIN" && game.reward > 0 && session.me) {
      session.setStars(session.me.stars + game.reward);
    }
    if (applyResult && !game.privateRoom && outcome && game.opponent) {
      const o = game.opponent;
      game.setOpponent({
        ...o,
        countWins: o.countWins + (outcome === "LOSE" ? 1 : 0),
        countLosses: o.countLosses + (outcome === "WIN" ? 1 : 0),
        gamesPlayed: o.gamesPlayed + 1,
      });
    }
    // Исход и имя проигравшего скрыты до раскрытия после интриги.
    elapsed.value = sessionSeconds();
    checking.value = false;
    checkingSource.value = null;
    clearAllTimers();
    if (monitor) monitor.stop();
    if (recording) recording.stop();
    phase.value = "postround";
    game.setPhase("postround");
    postRound.begin(message.postRound);
    if (pendingPostRound) {
      postRound.applySnapshot(pendingPostRound);
      pendingPostRound = null;
    }
  }

  /* ---------------------------------------------------------- завершение */

  /** Остановить всё (идемпотентно). Модель распознавания остаётся загруженной. */
  function teardown() {
    if (tornDown) return;
    tornDown = true;
    releaseDiagnostics();
    disposed = true;
    stopAudioWatch();
    roundSounds.dispose();
    alertSounds.dispose();
    stopInterfaceSound("found");
    postRound.stop();
    if (recording) { recording.dispose(); recording = null; }
    offs.forEach((off) => off());
    offs = [];
    clearAllTimers();
    if (monitor) {
      monitor.stop();
      monitor = null;
    }
    if (peer) {
      peer.close();
      peer = null;
    }
    if (localStream && trackEndedHandler) {
      localStream.getVideoTracks().forEach((track) => track.removeEventListener("ended", trackEndedHandler));
    }
    trackEndedHandler = null;
    if (unmuteHandler) {
      document.removeEventListener("pointerdown", unmuteHandler, true);
      unmuteHandler = null;
    }
    [localVideo.value, remoteVideo.value].forEach((video) => {
      if (!video) return;
      try {
        video.pause();
      } catch (e) {
        // видео уже остановлено
      }
      video.srcObject = null;
    });
    localStream = null;
    releaseCamera();
    telegram.closingConfirmation(false);
  }

  /** Игра для этого экрана закончена (дальше — только навигация). */
  function finish() {
    finished = true;
    checking.value = false;
    teardown();
  }

  function goHome() {
    clearTimeout(homeTimer);
    homeTimer = null;
    router.replace("/");
  }

  /**
   * Показать оверлей завершения игры («Соперник покинул игру», «Связь потеряна» …).
   * @param {{icon?:string, tone?:string, title:string, text?:string, autoHomeMs?:number}} info
   */
  function endGame(info) {
    if (finished) return;
    if (roundFinal) {
      game.postRoundNotice = info.title;
      finish();
      router.replace("/result");
      return;
    }
    phase.value = "ended";
    game.setEndReason(info.title);
    finish();
    telegram.haptic("warning");
    overlay.value = {
      kind: "ended",
      icon: info.icon || "warning",
      tone: info.tone || "neutral",
      title: info.title,
      text: info.text || "",
      autoMs: info.autoHomeMs || 0,
      actions: [{ id: "home", label: "На главную" }],
    };
    if (info.autoHomeMs) homeTimer = setTimeout(goHome, info.autoHomeMs);
  }

  /** «Уйти» (после подтверждения): leave_game → очистка → «/». */
  function leaveGame() {
    if (leaving) return;
    leaving = true;
    if (!finished && uuid && !preview) realtime.send({ type: "leave_game", gameUUID: uuid });
    finish();
    router.replace(roundFinal ? "/result" : "/");
  }

  function abandon(text) {
    if (finished) return;
    realtime.send({ type: "leave_game", gameUUID: uuid });
    toast.error(text);
    finish();
    router.replace("/");
  }

  /* ------------------------------------------------------------ медиа */

  function attachLocal(stream) {
    const video = localVideo.value;
    if (!video) return;
    video.muted = true;
    if (video.srcObject !== stream) video.srcObject = stream;
    video.play().catch(() => {
      // muted-видео с autoplay запустится само
    });
  }

  function playRemote() {
    const video = remoteVideo.value;
    if (!video) return;
    video.volume = REMOTE_VOLUME;
    video.play().catch((error) => {
      if (disposed || !error || error.name !== "NotAllowedError") return;
      // Автовоспроизведение со звуком запрещено: играем без звука, звук — по первому касанию.
      video.muted = true;
      video.play().catch(() => {
        // повторим по касанию
      });
      if (!unmuteHandler) {
        unmuteHandler = () => {
          document.removeEventListener("pointerdown", unmuteHandler, true);
          unmuteHandler = null;
          const v = remoteVideo.value;
          if (!v || disposed) return;
          v.muted = false;
          v.volume = REMOTE_VOLUME;
          v.play().catch(() => {
            // оставляем без звука
          });
        };
        document.addEventListener("pointerdown", unmuteHandler, true);
      }
    });
  }

  function attachRemote(stream) {
    const video = remoteVideo.value;
    if (!video || !stream) return;
    if (video.srcObject !== stream) video.srcObject = stream;
    playRemote();
  }

  function onRemoteFrame() {
    remoteVisible.value = true;
  }

  function watchLocalTrack(stream) {
    trackEndedHandler = () => {
      if (finished) return;
      if (phase.value === "intro") {
        realtime.send({ type: "quality_report", status: "QUALITY_FAILED", gameUUID: uuid });
      } else {
        realtime.send({ type: "leave_game", gameUUID: uuid });
      }
      endGame({ icon: "camera", tone: "danger", title: "Камера отключилась", text: "Без камеры продолжить игру нельзя." });
    };
    stream.getVideoTracks().forEach((track) => track.addEventListener("ended", trackEndedHandler));
  }

  async function setupMedia() {
    try {
      localStream = await acquireCamera();
    } catch (error) {
      if (disposed) return;
      realtime.send({ type: "quality_report", status: "QUALITY_FAILED", gameUUID: uuid });
      endGame({ icon: "camera", tone: "danger", title: "Камера недоступна", text: cameraErrorMessage(error) });
      return;
    }
    if (disposed) return;
    attachLocal(localStream);
    recording = createRoundRecording(localVideo.value);
    recording.start();
    cameraReady.value = true;
    watchLocalTrack(localStream);
    maybeStartMonitor();
    try {
      await peer.start(localStream);
    } catch (error) {
      if (disposed) return;
      console.error("[game] RTCPeerConnection failed", error);
      realtime.send({ type: "quality_report", status: "QUALITY_FAILED", gameUUID: uuid });
      endGame({
        icon: "warning",
        tone: "danger",
        title: "Видеосвязь недоступна",
        text: "Браузер не смог установить видеосвязь. Обновите Telegram или откройте игру на другом устройстве.",
      });
    }
  }

  /* -------------------------------------------------------------- WebRTC */

  function createPeer() {
    return createPeerSession({
      gameUUID: uuid,
      initiator,
      onRemoteStream: attachRemote,
      onState: (state) => {
        rtcState.value = state;
      },
      onProgress: (stage) => {
        const order = ["none", "peer", "offer", "remote", "connected"];
        if (order.indexOf(stage) > order.indexOf(rtcStage.value)) rtcStage.value = stage;
        if (stage === "connected" && phase.value === "intro" && !readyReceived.value) scheduleReadyResync();
      },
      onConnectTimeout: () => {
        endGame({
          icon: "warning",
          tone: "danger",
          title: "Не удалось соединиться",
          text: "Видеосвязь с соперником не установилась за 30 секунд. Попробуйте сыграть ещё раз.",
        });
      },
      onConnectionLost: () => {
        realtime.send({ type: "leave_game", gameUUID: uuid });
        endGame({
          icon: "warning",
          tone: "danger",
          title: "Связь с соперником потеряна",
          text: "Видеосвязь прервалась и не восстановилась.",
        });
      },
    });
  }

  /** Соединились, но READY нет: повторяем готовность и спрашиваем состояние (сообщения могли потеряться). */
  function scheduleReadyResync() {
    if (readyResyncTimer) return;
    readyResyncTimer = later(() => {
      readyResyncTimer = null;
      if (finished || phase.value !== "intro" || readyReceived.value) return;
      sendReadiness();
      syncGame();
      scheduleReadyResync();
    }, READY_RESYNC_MS);
  }

  /* ----------------------------------------------------------- детекция */

  async function loadModel() {
    modelStatus.value = "loading";
    if (overlay.value && overlay.value.kind === "model") overlay.value = null;
    try {
      await loadFaceLandmarker();
    } catch (error) {
      if (disposed) return;
      console.error("[game] FaceLandmarker load failed", error);
      modelStatus.value = "error";
      // Не загрузился JS-чанк (приложение обновили, пока оно было открыто): повтор запросит тот же
      // удалённый файл — предлагаем перезагрузку.
      const outdated = isChunkLoadError(error);
      overlay.value = {
        kind: "model",
        icon: "warning",
        tone: "danger",
        title: "Распознавание лица не загрузилось",
        text: outdated
          ? "Вышла новая версия игры. Обновите приложение и начните игру заново."
          : "Проверьте интернет и попробуйте ещё раз.",
        autoMs: 0,
        actions: [
          outdated
            ? { id: "reload-app", label: "Обновить приложение", icon: "refresh" }
            : { id: "retry-model", label: "Повторить", icon: "refresh" },
          { id: "leave", label: "Уйти", variant: "secondary" },
        ],
      };
      return;
    }
    if (disposed) return;
    modelStatus.value = "ready";
    sendDeviceStatus("ML_READY");
    maybeStartMonitor();
  }

  /** Dev tools: метрики для панели под кнопкой «Simulate violation» (пики за интервал обновления). */
  function onDevMetrics(metrics) {
    if (!metrics.face) {
      devPeak = null;
      devMetrics.value = { face: false };
      return;
    }
    devPeak = devPeak
      ? {
          blinkScore: Math.max(devPeak.blinkScore, metrics.blinkScore),
          jawOpen: Math.min(devPeak.jawOpen, metrics.jawOpen),
          ear: Math.min(devPeak.ear, metrics.ear === null ? 1 : metrics.ear),
          mar: Math.min(devPeak.mar, metrics.mar === null ? 1 : metrics.mar),
        }
      : { blinkScore: metrics.blinkScore, jawOpen: metrics.jawOpen, ear: metrics.ear === null ? 1 : metrics.ear, mar: metrics.mar === null ? 1 : metrics.mar };
    const now = performance.now();
    if (now - devMetricsAt < 250) return;
    devMetricsAt = now;
    devMetrics.value = {
      ...devPeak,
      face: true,
      thresholds: metrics.thresholds,
      calibrated: metrics.calibrated,
      frameMs: metrics.frameMs,
      gapMs: metrics.gapMs,
    };
    devPeak = null;
  }

  function maybeStartMonitor() {
    if (monitor || disposed || modelStatus.value !== "ready" || !cameraReady.value || !localVideo.value) return;
    monitor = createViolationMonitor({
      video: localVideo.value,
      mode,
      onFace: (present) => {
        faceDetected.value = present;
      },
      // Условие литеральное: в прод-сборке метрики не собираются.
      onMetrics: process.env.VUE_APP_DEV_TOOLS === "true" ? onDevMetrics : undefined,
      onViolation: (violation) => {
        if (phase.value !== "playing" || checking.value || finished) return;
        reportViolation(violation);
      },
      onError: () => {
        if (detectorWarned || finished) return;
        detectorWarned = true;
        toast.error("Распознавание лица работает с перебоями");
      },
    });
    monitor.start();
    if (phase.value === "calibration") monitor.calibrate(CALIBRATION_MS);
    else if (phase.value === "playing" && !checking.value) monitor.arm();
  }

  function sessionSeconds() {
    return playStartedAt ? Math.floor((Date.now() - playStartedAt) / 1000) : 0;
  }

  /**
   * Нарушение (своё): кадр → бинарное сообщение (CONTRACT §3.3) → «Проверка…» до ответа сервера.
   * @param {{jpeg: Promise<Blob>, score: number}} violation
   */
  async function reportViolation({ jpeg, score, regions, timestamp = Date.now(), mediaTime = localVideo.value?.currentTime }) {
    // Фиксируем буфер до await: кадры после нарушения не подменяют доказательство.
    const recorded = recording ? recording.snapshot(jpeg, timestamp, mediaTime).catch(() => null) : Promise.resolve(null);
    beginChecking("me");
    telegram.haptic("heavy");
    let blob = null;
    try {
      blob = await jpeg;
    } catch (error) {
      blob = null;
    }
    if (disposed || !checking.value) return;
    if (!blob) {
      toast.error("Не удалось снять кадр, продолжаем");
      deferResume();
      return;
    }
    const evidence = await recorded;
    if (disposed || !checking.value || roundFinal) return;
    const frame = buildRecordedViolation({ detectionType: mode, gameUUID: uuid, sessionTime: sessionSeconds(), score, timestamp, regions }, blob, evidence);
    let sent = realtime.sendBinary(frame);
    if (!sent) {
      try {
        await realtime.whenOpen(10000);
      } catch (error) {
        // ниже — продолжаем игру
      }
      if (disposed || !checking.value) return;
      sent = realtime.sendBinary(frame);
    }
    if (!sent) {
      toast.error("Нет связи с сервером, продолжаем");
      deferResume();
    }
  }

  function giveUpChecking() {
    watchdogTimer = null;
    if (checking.value) abandon("Сервер не ответил. Игра прервана");
  }

  function startWatchdog() {
    cancel(watchdogTimer);
    checkingSince = Date.now();
    checkingSyncSent = false;
    checkingExtended = false;
    watchdogTimer = later(() => {
      // 20 c без ответа: спрашиваем состояние; ещё 10 c — выходим.
      checkingSyncSent = true;
      syncGame();
      watchdogTimer = later(giveUpChecking, PROCESSING_GIVE_UP_MS);
    }, PROCESSING_SYNC_MS);
  }

  /**
   * На sync_game сервер ответил PROCESSING — проверка ещё идёт (медленный MinIO/ML), это ответ, а не тишина.
   * Продлеваем ожидание один раз (+20 c), но не дальше 50 c от начала проверки: если блокировка на сервере
   * зависла, «Проверка…» не будет вечной.
   */
  function extendWatchdog() {
    if (!checking.value || !checkingSyncSent || checkingExtended) return;
    checkingExtended = true;
    const left = checkingSince + PROCESSING_MAX_MS - Date.now();
    if (left <= 0) return;
    cancel(watchdogTimer);
    watchdogTimer = later(giveUpChecking, Math.min(PROCESSING_EXTEND_MS, left));
  }

  function endAlert() {
    alertTimer = null;
    if (finished) return;
    alertVisible.value = false;
    if (pendingFinal) {
      const final = pendingFinal;
      pendingFinal = null;
      const state = final.postRound;
      // A verdict held behind the alert still carries the original receipt-time server clock.
      onFinal(state && Number.isFinite(state.serverNow) ? { ...final, postRound: { ...state,
        serverNow: state.serverNow + Date.now() - pendingFinalReceivedAt } } : final);
    } else if (pendingResume) {
      pendingResume = false;
      resumePlay();
    }
  }

  function updateAlertDeadline(message) {
    if (!alertVisible.value || !Number.isFinite(message?.alertEndsAt) || !Number.isFinite(message?.serverNow)) return;
    // Server confirms one detection window; duplicate PROCESSING must not restart it.
    if (alertServerEnd !== null) return;
    alertServerEnd = message.alertEndsAt;
    const end = Date.now() + Math.max(0, message.alertEndsAt - message.serverNow);
    if (Math.abs(end - alertEndsAt) <= 150) return;
    alertEndsAt = end;
    cancel(alertTimer);
    alertTimer = later(endAlert, Math.max(0, end - Date.now()));
  }

  function beginChecking(source, message) {
    processingIdentity = Number.isFinite(message?.processingStartedAt) ? message.processingStartedAt : null;
    checking.value = true;
    checkingSource.value = source;
    pendingResume = false;
    game.setPhase("processing");
    roundSounds.stopTicks();
    if (monitor) {
      monitor.disarm();
      if (source === "me") monitor.stop();
    }
    const remaining = Number.isFinite(message?.alertEndsAt) && Number.isFinite(message?.serverNow)
      ? Math.max(0, message.alertEndsAt - message.serverNow) : ALERT_MS;
    alertServerEnd = Number.isFinite(message?.alertEndsAt) ? message.alertEndsAt : null;
    alertEndsAt = Date.now() + remaining;
    alertVisible.value = remaining > 0;
    if (alertVisible.value) alertSounds.play("signal");
    cancel(alertTimer);
    alertTimer = later(endAlert, remaining);
    startWatchdog();
  }

  function deferResume() {
    if (pendingFinal || finished) return;
    cancel(watchdogTimer);
    watchdogTimer = null;
    if (alertVisible.value) pendingResume = true;
    else resumePlay();
  }

  function resumePlay() {
    if (finished) return;
    if (Number.isFinite(processingIdentity)) lastCompletedProcessing = Math.max(lastCompletedProcessing, processingIdentity);
    checking.value = false;
    alertVisible.value = false;
    pendingResume = false;
    checkingSource.value = null;
    cancel(watchdogTimer);
    watchdogTimer = null;
    checkingSyncSent = false;
    if (recording) recording.start();
    game.setPhase(phase.value === "playing" ? "playing" : "calibration");
    if (!monitor) return;
    monitor.start();
    if (phase.value === "playing") {
      monitor.arm();
      roundSounds.startTicks(playStartedAt);
    }
  }

  /* ---------------------------------------------------------------- фазы */

  function maybeStartCalibration() {
    if (finished || phase.value !== "intro" || !readyReceived.value || preparationStarted) return;
    preparationStarted = true;
    const at = preparationAt - preparationOffset;
    later(() => {
      if (finished || phase.value !== "intro") return;
      phase.value = "instruction";
      game.setPhase("instruction");
      later(() => startCalibration(at + INSTRUCTION_MS), Math.max(0, at + INSTRUCTION_MS - Date.now()));
    }, Math.max(0, at - Date.now()));
  }

  function startCalibration(startedAt) {
    if (finished || playStartedAt) return;
    phase.value = "calibration";
    game.setPhase("calibration");
    telegram.haptic("medium");
    countdown.value = COUNTDOWN_SECONDS;
    countdownProgress.value = 0;
    if (monitor) {
      monitor.start();
      monitor.calibrate(Math.max(100, startedAt + CALIBRATION_MS - Date.now()));
    }
    const total = COUNTDOWN_SECONDS * 1000;
    const update = () => {
      const ms = Math.max(0, Date.now() - startedAt);
      countdownProgress.value = Math.min(1, ms / total);
      const left = Math.max(1, COUNTDOWN_SECONDS - Math.floor(ms / 1000));
      if (left !== countdown.value) {
        countdown.value = left;
        telegram.haptic("light");
      }
      if (ms >= total) {
        stopEvery(countdownInterval);
        countdownInterval = null;
        if (!calibrationReady) {
          calibrationReady = true;
          sendDeviceStatus("CALIBRATION_READY");
          later(() => {
            if (finished || playStartedAt) return;
            sendDeviceStatus("CALIBRATION_READY");
            syncGame();
          }, READY_RESYNC_MS);
        }
      }
    };
    countdownInterval = every(update, 50);
    update();
  }

  function startPlaying(startedAt) {
    if (finished || playStartedAt || roundFinal) return;
    stopEvery(countdownInterval);
    countdownInterval = null;
    phase.value = "playing";
    game.setPhase(checking.value ? "processing" : "playing");
    playStartedAt = startedAt;
    elapsed.value = sessionSeconds();
    // Fresh scheduled start emits once; old sync responses do not replay the gong.
    if (Date.now() - startedAt <= 500) {
      roundSounds.play("start");
      startBanner.value = true;
      later(() => { startBannerFading.value = true; }, 1000);
      later(() => { startBanner.value = false; }, 1500);
    }
    roundSounds.startTicks(playStartedAt);
    every(() => { elapsed.value = sessionSeconds(); }, 250);
    telegram.haptic("heavy");
    if (monitor && !checking.value) {
      monitor.start();
      monitor.arm();
    }
  }

  /* ------------------------------------------------------------- действия */

  /** Dev: нарушение с текущим кадром по обычному пути (работает и с настоящим bunker). */
  function simulateViolation() {
    if (preview) {
      checking.value = !checking.value;
      checkingSource.value = "me";
      return;
    }
    if (phase.value !== "playing" || checking.value || finished) {
      toast.info("Доступно во время игры");
      return;
    }
    if (monitor) monitor.disarm();
    reportViolation({ jpeg: captureFrameJpeg(localVideo.value), score: 1 });
  }

  /** Перезагрузить приложение (новая версия после деплоя); игру перед этим покидаем явно. */
  function reloadApp() {
    if (!finished && uuid && !preview) realtime.send({ type: "leave_game", gameUUID: uuid });
    finish();
    window.location.reload();
  }

  function onOverlayAction(id) {
    if (id === "home") goHome();
    else if (id === "leave") leaveGame();
    else if (id === "retry-model") loadModel();
    else if (id === "reload-app") reloadApp();
  }

  /* -------------------------------------------------------- dev: превью */

  async function startPreview() {
    const seeded = game.phase;
    if (game.postRoundSnapshot) {
      roundFinal = true;
      phase.value = "postround";
      offs.push(realtime.on("post_round", onPostRound));
      postRound.begin(game.postRoundSnapshot);
    }
    if (!roundFinal) phase.value = seeded === "processing" ? "playing" : ["intro", "calibration", "playing"].includes(seeded) ? seeded : "intro";
    checking.value = seeded === "processing";
    checkingSource.value = checking.value ? "opponent" : null;
    cameraReady.value = true;
    modelStatus.value = "ready";
    rtcStage.value = phase.value === "intro" ? "remote" : "connected";
    readyReceived.value = phase.value !== "intro";
    faceDetected.value = true;
    countdown.value = 3;
    countdownProgress.value = 0.45;
    if (phase.value === "playing") {
      playStartedAt = Date.now() - PREVIEW_ELAPSED * 1000;
      elapsed.value = PREVIEW_ELAPSED;
      every(() => {
        elapsed.value = sessionSeconds();
      }, 250);
    }
    try {
      localStream = await acquireCamera();
      if (disposed) return;
      attachLocal(localStream);
    } catch (error) {
      // превью без камеры
    }
    // Условие литеральное, чтобы webpack вырезал dev-чанк из прод-сборки.
    if (process.env.VUE_APP_MOCK_API === "true") {
      const { mockOpponentStream } = await import(/* webpackChunkName: "dev-mock" */ "@/dev/mockApi");
      if (disposed) return;
      try {
        attachRemote(mockOpponentStream());
      } catch (error) {
        // фейковая камера соперника недоступна
      }
    }
  }

  /* ----------------------------------------------------------- жизненный цикл */

  async function loadOpponent() {
    if (game.opponent) return;
    for (let attempt = 0; attempt < 3 && !disposed; attempt += 1) {
      try {
        const info = await getOpponentInfo(uuid);
        if (!disposed && info) game.setOpponent(info);
        return;
      } catch (error) {
        await new Promise((resolve) => setTimeout(resolve, 1200));
      }
    }
  }

  onMounted(() => {
    if (preview) {
      startPreview();
      return;
    }
    if (!uuid) {
      router.replace("/");
      return;
    }
    // Подписки — до любых await и до создания RTCPeerConnection.
    offs.push(realtime.on("game_status", onGameStatus), realtime.on("post_round", onPostRound), realtime.on("reconnected", onReconnected));
    peer = createPeer();
    game.setPhase("intro");
    telegram.closingConfirmation(true);

    later(() => {
      waitingLong.value = true;
    }, INTRO_SLOW_MS);
    later(() => {
      if (!playStartedAt && !roundFinal && !finished) abandon("Не удалось начать игру. Попробуйте ещё раз");
    }, INTRO_TIMEOUT_MS);

    sendDeviceStatus("WS_READY");
    // Соперник мог уйти, пока мы переходили с экрана поиска (OPPONENT_LEFT пришёл без подписчика):
    // сервер ответит WAITING или OPPONENT_LEFT.
    syncGame();
    loadOpponent();
    setupMedia();
    loadModel();
  });

  onBeforeUnmount(() => {
    // Ушли с экрана, не закончив игру (например, критический экран) — сообщаем серверу.
    if (!finished && !preview && uuid) realtime.send({ type: "leave_game", gameUUID: uuid });
    finished = true;
    clearTimeout(homeTimer);
    homeTimer = null;
    teardown();
  });

  return {
    ...postRound,
    // данные
    mode,
    preview,
    me,
    opponent,
    phase,
    checking,
    checkingSource,
    alertVisible,
    startBanner,
    startBannerFading,
    countdown,
    countdownProgress,
    elapsed,
    faceDetected,
    faceWarning,
    cameraReady,
    modelStatus,
    remoteVisible,
    rtcState,
    connectionProgress,
    introStatus,
    waitingLong,
    overlay,
    devMetrics,
    // действия
    leaveGame,
    simulateViolation,
    onOverlayAction,
    onRemoteFrame,
  };
}

export default useGameSession;
